/**
 * Signal Path Engine — the quiet brain of Authentic Momentum.
 *
 * Reads the visitor's local behavior (which sections they touched,
 * which Journal posts they read, which CTAs they clicked) and the
 * existing `conversionState` flags, scores them across ten signal
 * categories, and returns the next aligned move the rest of the
 * site can render.
 *
 * Design principles:
 *   - All state is localStorage-only. Nothing leaves the browser.
 *   - We score, never log private text. No quiz answers in here.
 *   - Low confidence → default to Snapshot. We don't pretend.
 *   - The engine is a pure function over local state; UI components
 *     subscribe via getSignalSnapshot() and re-evaluate on mount.
 *   - Copy never says "we noticed", "based on your behavior", or
 *     "tracking". The visitor experience is helpful, not watched.
 */

import { getConversionState } from "./conversionState";

const KEY = "am_signal_path_v1";

const DEFAULT_STATE = {
  // Section impressions (how many times each major section came into view)
  sections: {},
  // Journal posts read (slug → count)
  journalReads: {},
  // Aggregate tag engagement from journal reading (tag → count)
  tagEngagement: {},
  // CTA clicks ({name: count})
  ctaClicks: {},
  // First and last touch timestamps
  firstSeen: null,
  lastSeen: null,
};

const safeRead = () => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT_STATE };
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_STATE, ...parsed };
  } catch {
    return { ...DEFAULT_STATE };
  }
};

const safeWrite = (state) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    /* no-op */
  }
};

const stampedWrite = (mutator) => {
  const state = safeRead();
  if (!state.firstSeen) state.firstSeen = new Date().toISOString();
  state.lastSeen = new Date().toISOString();
  mutator(state);
  safeWrite(state);
};

// ──────────────────────────────────────────────────────────────
// Recorders — small, single-purpose functions called from the
// existing components. None of them know about scoring; they just
// nudge the underlying counters.
// ──────────────────────────────────────────────────────────────
export const recordSectionView = (id) => {
  if (!id) return;
  stampedWrite((s) => {
    s.sections[id] = (s.sections[id] || 0) + 1;
  });
};

export const recordJournalRead = (slug, themeTags = []) => {
  if (!slug) return;
  stampedWrite((s) => {
    s.journalReads[slug] = (s.journalReads[slug] || 0) + 1;
    themeTags.forEach((t) => {
      const k = String(t).toLowerCase();
      s.tagEngagement[k] = (s.tagEngagement[k] || 0) + 1;
    });
  });
};

export const recordCtaClick = (name) => {
  if (!name) return;
  stampedWrite((s) => {
    s.ctaClicks[name] = (s.ctaClicks[name] || 0) + 1;
  });
};

// ──────────────────────────────────────────────────────────────
// Tag → signal mapping. Pulled straight from the sprint brief.
// ──────────────────────────────────────────────────────────────
const TAG_SIGNAL_MAP = {
  clarity: "clarity",
  energy: "energy",
  identity: "identity",
  momentum: "blueprint",
  strategy: "strategy",
  signal: "snapshot",
  focus: "clarity",
  boundaries: "energy",
  direction: "clarity",
  stillness: "energy",
  resistance: "identity",
  alignment: "blueprint",
  confidence: "identity",
  "self-trust": "identity",
  accountability: "snapshot",
  timing: "clarity",
  fear: "identity",
  authenticity: "identity",
  // Volume II diversity categories
  transition: "clarity",
  healing: "identity",
  relationships: "identity",
  reflection: "snapshot",
  purpose: "blueprint",
  "inner work": "identity",
  discipline: "blueprint",
};

const ARCHETYPE_SIGNAL = {
  clarifier: "clarity",
  restorer: "energy",
  builder: "structure",
  messenger: "expression",
  alchemist: "expression",
  integrator: "structure",
};

const ARCHETYPE_PATH = {
  clarifier: "Clarify",
  restorer: "Clarify",
  builder: "Build",
  messenger: "Express",
  alchemist: "Express",
  integrator: "Scale",
};

const SIGNAL_TO_PATH = {
  clarity: "Clarify",
  identity: "Clarify",
  energy: "Clarify",
  book: "Clarify",
  blueprint: "Clarify",
  structure: "Build",
  strategy: "Build",
  expression: "Express",
  consultation: "Scale",
  snapshot: "Clarify",
};

  // Per-signal copy + CTA for the SignalGuidanceCard. Tone: elegant,
// brand-aligned, never "we noticed", never "based on behavior".
const SIGNAL_COPY = {
  orientation_reset: {
    eyebrow: "Some questions are ready for a conversation",
    line: "Bring the one you keep circling. The Clarity Reset Session names the hidden frame and clarifies the next aligned move.",
    cta: "Book the Clarity Reset Session",
    href: null, // opens consultation modal
  },
  clarity: {
    eyebrow: "The next signal may be clarity",
    line: "Start with the Snapshot and let the next step become visible.",
    cta: "Find Your Current Signal",
    href: "/frequency-snapshot",
  },
  energy: {
    eyebrow: "The next signal may be energy",
    line: "The framework begins in the book. Read the first transmission and see if the mirror is yours.",
    cta: "Get the Book",
    href: "/book",
  },
  identity: {
    eyebrow: "The next signal may be identity",
    line: "Your answers may already contain the structure. The Personal Action Plan helps organize what is trying to become clear.",
    cta: "Start the Personal Action Plan",
    href: "/?checkout=blueprint",
  },
  strategy: {
    eyebrow: "The next signal may be strategy",
    line: "Some questions are ready for a focused 30-minute conversation. The Clarity Reset Session names the frame before the work.",
    cta: "Book the Clarity Reset Session · $149",
    href: "/orientation-reset/schedule",
  },
  expression: {
    eyebrow: "The next signal may be expression",
    line: "If your voice is the move, begin with the Express path. The Blueprint refines what is already trying to come forward.",
    cta: "Explore Your Path",
    href: "/start-here",
  },
  structure: {
    eyebrow: "The next signal may be structure",
    line: "The pieces are present. The next move is organization. Begin with Build, then turn signal into structure.",
    cta: "Explore Your Path",
    href: "/start-here",
  },
  consultation: {
    eyebrow: "The frame is ready to be named",
    line: "Bring the question you keep circling. The Clarity Reset Session turns it into clarity.",
    cta: "Book the Clarity Reset Session · $149",
    href: "/orientation-reset/schedule",
  },
  book: {
    eyebrow: "The framework begins in the book",
    line: "Read the first transmission, then decide if the mirror is yours.",
    cta: "Get the Book",
    href: "/book",
  },
  blueprint: {
    eyebrow: "Your answers may already contain the structure",
    line: "The Blueprint helps organize what is trying to come forward.",
    cta: "Start the Blueprint",
    href: "/?checkout=blueprint",
  },
  snapshot: {
    eyebrow: "The next signal may already be visible",
    line: "The Snapshot is a mirror before the map. Begin there.",
    cta: "Find Your Current Signal",
    href: "/frequency-snapshot",
  },
};

// ──────────────────────────────────────────────────────────────
// Circular Behavior detection.
//
// Some visitors orbit a question they have not named yet. They open
// the Blueprint page, drift to Services, return to identity-tagged
// Journal posts, but never commit. The site reads that loop as a
// quiet "Orientation Reset" signal — the moment a focused $149
// conversation will help more than another page view.
//
// Detection criteria (any TWO of the following + zero conversion):
//   1. blueprint section impressions >= 2
//   2. services section impressions >= 1
//   3. identity/resistance/alignment journal tag reads >= 3 total
//   4. >= 2 distinct journal posts read AND >= 1 services view
//
// "No conversion" means blueprint not started AND blueprint not
// submitted AND no consultation has been opened. (The frontend can
// observe blueprintStarted/Submitted from conversionState. Consultation-
// open is local-only via a counter the consultation modal increments
// — we treat any modal open as enough movement to no longer be "orbiting".)
// ──────────────────────────────────────────────────────────────
const IDENTITY_TAGS = new Set([
  "identity",
  "resistance",
  "confidence",
  "self-trust",
  "fear",
  "authenticity",
  "alignment",
]);

export const detectCircularBehavior = (conv, eng) => {
  const c = conv || getConversionState();
  const e = eng || safeRead();

  // Hard exclusions — anyone who has already taken a paid step is by
  // definition no longer orbiting.
  if (c.blueprintStarted || c.blueprintSubmitted) return null;
  if (c.consultationOpened) return null;

  const blueprintViews = e.sections?.blueprint || 0;
  const servicesViews = e.sections?.services || 0;
  const identityTagReads = Object.entries(e.tagEngagement || {}).reduce(
    (acc, [tag, count]) =>
      IDENTITY_TAGS.has(tag) ? acc + count : acc,
    0
  );
  const distinctJournalReads = Object.keys(e.journalReads || {}).length;

  const criteria = [
    blueprintViews >= 2,
    servicesViews >= 1,
    identityTagReads >= 3,
    distinctJournalReads >= 2 && servicesViews >= 1,
  ];
  const hits = criteria.filter(Boolean).length;
  if (hits < 2) return null;

  // Build a human-readable reason so the admin and the visitor's
  // engine snapshot can describe what was observed.
  const reasonFragments = [];
  if (blueprintViews >= 2) reasonFragments.push(`returned to Blueprint ${blueprintViews}×`);
  if (servicesViews >= 1) reasonFragments.push(`viewed Four Paths`);
  if (identityTagReads >= 3) reasonFragments.push(`${identityTagReads} identity-layer reads`);
  if (distinctJournalReads >= 2) reasonFragments.push(`${distinctJournalReads} journal entries`);

  return {
    detected: true,
    strength: hits, // 2 | 3 | 4
    reason: reasonFragments.join(", "),
    sections_orbited: { blueprint: blueprintViews, services: servicesViews },
    identity_tag_reads: identityTagReads,
    distinct_journal_reads: distinctJournalReads,
  };
};


// ──────────────────────────────────────────────────────────────
// Visitor state classification — used by the Final CTA, the
// admin Opportunity Signals panel, and the engine itself.
// ──────────────────────────────────────────────────────────────
export const classifyVisitor = (conv, sig) => {
  const eng = sig || safeRead();
  const c = conv || getConversionState();
  if (c.blueprintSubmitted) return "blueprint_submitted";
  if (c.blueprintStarted) return "blueprint_started";
  if (c.snapshotCompleted && !c.bookPurchased) return "snapshot_completed";
  if (c.bookPurchased) return "book_buyer";
  if (eng.sections.book || c.bookPreviewed) return "book_curious";
  if (Object.keys(eng.journalReads).length > 0) return "reader";
  return "cold";
};

const VISITOR_PRIMARY_ACTION = {
  cold: "snapshot",
  reader: "snapshot",
  book_curious: "book",
  book_buyer: "blueprint",
  snapshot_completed: "blueprint",
  blueprint_started: "blueprint",
  blueprint_submitted: "consultation",
};

// ──────────────────────────────────────────────────────────────
// Score all signals from the raw inputs.
// ──────────────────────────────────────────────────────────────
const scoreSignals = (conv, eng) => {
  const scores = {
    clarity: 0,
    energy: 0,
    identity: 0,
    strategy: 0,
    expression: 0,
    structure: 0,
    consultation: 0,
    book: 0,
    blueprint: 0,
    snapshot: 0,
  };

  // Tag engagement — strongest journal-side signal
  Object.entries(eng.tagEngagement || {}).forEach(([tag, count]) => {
    const sig = TAG_SIGNAL_MAP[tag];
    if (sig && sig in scores) scores[sig] += count * 2;
  });

  // Section impressions
  if (eng.sections.book) scores.book += eng.sections.book;
  if (eng.sections.blueprint) scores.blueprint += eng.sections.blueprint;
  if (eng.sections.services) scores.expression += 1; // browsing the Four Paths leans expression/structure
  if (eng.sections.showcase) scores.expression += 1;
  if (eng.sections.consultation) scores.consultation += 2;

  // Hard conversion signals
  if (conv.bookPreviewed) scores.book += 3;
  if (conv.bookPurchased) {
    scores.book += 5;
    scores.blueprint += 4;
  }
  if (conv.snapshotCompleted) {
    scores.snapshot += 5;
    scores.blueprint += 3;
    if (conv.snapshotResult && conv.snapshotResult.archetype) {
      const a = String(conv.snapshotResult.archetype).toLowerCase();
      const arSig = ARCHETYPE_SIGNAL[a];
      if (arSig in scores) scores[arSig] += 4;
    }
  }
  if (conv.blueprintStarted) {
    scores.blueprint += 6;
    scores.consultation += 1;
  }
  if (conv.blueprintSubmitted) {
    scores.blueprint += 4;
    scores.consultation += 6;
  }

  // CTA clicks — moderate signal
  Object.entries(eng.ctaClicks || {}).forEach(([name, count]) => {
    const lc = name.toLowerCase();
    if (lc.includes("consult")) scores.consultation += count * 2;
    if (lc.includes("book")) scores.book += count;
    if (lc.includes("blueprint")) scores.blueprint += count;
    if (lc.includes("snapshot")) scores.snapshot += count;
  });

  return scores;
};

const rankSignals = (scores) =>
  Object.entries(scores)
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);

// ──────────────────────────────────────────────────────────────
// Public snapshot — what UI components consume.
// ──────────────────────────────────────────────────────────────
export const getSignalSnapshot = () => {
  const conv = getConversionState();
  const eng = safeRead();
  const scores = scoreSignals(conv, eng);
  const ranked = rankSignals(scores);
  const total = ranked.reduce((acc, [, v]) => acc + v, 0);

  const visitorState = classifyVisitor(conv, eng);
  const circular = detectCircularBehavior(conv, eng);

  let primarySignal = ranked[0] ? ranked[0][0] : null;
  let secondarySignal = ranked[1] ? ranked[1][0] : null;

  // Confidence — high if a single signal dominates and we have enough touches
  let confidenceLevel = "low";
  if (total >= 12 && ranked[0] && ranked[0][1] / total > 0.45) {
    confidenceLevel = "high";
  } else if (total >= 5) {
    confidenceLevel = "medium";
  }

  // Circular behavior is the strongest single signal we can detect.
  // When it fires, it overrides the score-based ranking — the visitor
  // is orbiting a question and needs a conversation, not another page.
  if (circular) {
    secondarySignal = primarySignal;
    primarySignal = "orientation_reset";
    confidenceLevel = "high";
  } else if (confidenceLevel === "low") {
    // Defensive fallback: when confidence is low (and we are not
    // circular), default to Snapshot.
    primarySignal = "snapshot";
    secondarySignal = secondarySignal || null;
  }

  // The recommended action is the intersection of (visitor state ×
  // primary signal). Visitor state wins when it implies a hard
  // forward step; signal wins for nuance/copy. Circular wins outright.
  const stateAction = VISITOR_PRIMARY_ACTION[visitorState];
  const recommendedNextAction = circular
    ? "orientation_reset"
    : stateAction || primarySignal;

  const guidance = SIGNAL_COPY[primarySignal] || SIGNAL_COPY.snapshot;
  const recommendedCTA = {
    label: guidance.cta,
    href: guidance.href,
    eyebrow: guidance.eyebrow,
    line: guidance.line,
  };

  const recommendedPath =
    (conv.snapshotResult &&
      ARCHETYPE_PATH[String(conv.snapshotResult.archetype || "").toLowerCase()]) ||
    SIGNAL_TO_PATH[primarySignal] ||
    null;

  return {
    primarySignal,
    secondarySignal,
    recommendedNextAction,
    recommendedCTA,
    recommendedPath,
    confidenceLevel,
    visitorState,
    circular,
    scores,
    raw: { eng, conv },
  };
};

// ──────────────────────────────────────────────────────────────
// Helper for the Journal post → signal-aware closing card.
// Returns the dominant signal across the post's tags so the
// closing card can speak directly to what the reader just
// engaged with — without ever saying "you read this".
// ──────────────────────────────────────────────────────────────
export const signalForTags = (themeTags = []) => {
  const buckets = {};
  themeTags.forEach((t) => {
    const sig = TAG_SIGNAL_MAP[String(t).toLowerCase()];
    if (sig) buckets[sig] = (buckets[sig] || 0) + 1;
  });
  const ranked = Object.entries(buckets).sort((a, b) => b[1] - a[1]);
  return ranked[0] ? ranked[0][0] : null;
};

export const guidanceForSignal = (signal) =>
  SIGNAL_COPY[signal] || SIGNAL_COPY.snapshot;

export const SIGNAL_NAMES = Object.keys(SIGNAL_COPY);

// Exposed for the admin "Opportunity Signals" panel.
export const VISITOR_STATE_LABELS = {
  cold: "Needs Nurture",
  reader: "Needs Nurture",
  book_curious: "Incomplete Journey",
  book_buyer: "Ready for Blueprint",
  snapshot_completed: "Ready for Blueprint",
  blueprint_started: "Incomplete Journey",
  blueprint_submitted: "Ready for Conversation",
};
